import {useState, useCallback, useMemo, useEffect} from 'react'
import {useBi, useEnvironment} from '@wix/yoshi-flow-editor'
import {TabItem} from 'wix-ui-tpa/cssVars'
import {Experience as ExperienceProto} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation as ReservationLocationProto} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {useExperiencesListStorage} from '../storage'
import {getLogger, PageType} from '../../../utils/getLogger'
import {AppPage} from '../../../utils/constants'

const ALL_RESERVATION_LOCATIONS_OPTION = 0

export const useHooks = ({
  allReservationLocationsTabText,
}: {
  allReservationLocationsTabText: string
}) => {
  const {isPreview} = useEnvironment()
  const bi = useBi()
  const logger = getLogger(bi)
  const {
    breadcrumbsConfig,
    regionalSettings,
    reservationLocations: allReservationLocations,
    experiences: allExperiences,
    goToExperienceDetailsPage,
  } = useExperiencesListStorage()

  const [selectedReservationLocationTabIndex, setSelectedReservationLocationTabIndex] = useState(
    ALL_RESERVATION_LOCATIONS_OPTION,
  )

  const {reservationLocationsMap, filteredExperiences} = useMemo(
    () => filterReservationLocationsAndExperiences(allReservationLocations, allExperiences),
    [allReservationLocations, allExperiences],
  )

  const reservationLocationTabOptions: TabItem[] = useMemo(() => {
    const allReservationLocationsOption: TabItem = {
      id: 'all',
      title: allReservationLocationsTabText,
    }

    const restReservationLocationOptions = Object.values(reservationLocationsMap).map(
      ({id, location}) => ({
        id: id!,
        title: location?.name!,
      }),
    )

    return [allReservationLocationsOption, ...restReservationLocationOptions]
  }, [allReservationLocationsTabText, reservationLocationsMap])

  const selectedExperiences =
    selectedReservationLocationTabIndex === ALL_RESERVATION_LOCATIONS_OPTION
      ? filteredExperiences
      : filteredExperiences.filter(
          ({reservationLocationId}) =>
            reservationLocationId ===
            reservationLocationTabOptions[selectedReservationLocationTabIndex].id,
        )

  const showEmptyState = selectedExperiences.length === 0

  const handleReservationLocationSelect = useCallback((index: number) => {
    setSelectedReservationLocationTabIndex(index)
  }, [])

  const handleGoToExperience = useCallback(
    (experienceId: string) => {
      goToExperienceDetailsPage(experienceId)
    },
    [goToExperienceDetailsPage],
  )

  useEffect(() => {
    logger.isLoaded({
      isAddOn: false,
      isPreview,
      pageType: PageType.page,
      pageName: AppPage.ExperiencesList,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    breadcrumbsConfig,
    regionalSettings,
    selectedReservationLocationTabIndex,
    reservationLocationTabOptions,
    selectedExperiences,
    showEmptyState,
    handleReservationLocationSelect,
    handleGoToExperience,
  }
}

const filterReservationLocationsAndExperiences = (
  reservationLocations: ReservationLocationProto[],
  experiences: ExperienceProto[],
) => {
  const unarchivedReservationLocations = reservationLocations.filter(({archived}) => !archived)
  const visibleExperiences = experiences.filter(({configuration}) => configuration?.visible)

  const reservationLocationsMap: Record<string, ReservationLocationProto> = {}

  const experiencesWithExistingReservationLocation = visibleExperiences.filter(
    ({reservationLocationId}) => {
      const experienceReservationLocation = unarchivedReservationLocations.find(
        ({id}) => id === reservationLocationId,
      )

      if (experienceReservationLocation) {
        reservationLocationsMap[experienceReservationLocation.id!] = experienceReservationLocation
        return true
      }

      return false
    },
  )

  return {
    reservationLocationsMap,
    filteredExperiences: experiencesWithExistingReservationLocation,
  }
}
